import { MediaType, ExternalAssetSource } from "../../../types";
import { BaseAsset, IAssetMetadata } from "../../base";
import { FontsourceListItem, FontVariantUrls, FontsourceDetailItem } from "../types";

/**
 * Resolve the best available preview URL for a font family from its variant map.
 * Prefers woff2 → woff → ttf from the Regular (400) / normal / default subset variant.
 */
function resolvePreviewUrl(item: FontsourceListItem & { variants?: any }): string {
  if (!item.variants) return '';
  const weight = '400';
  const style = 'normal';
  const subset = item.defSubset ?? 'latin';
  const urls: FontVariantUrls | undefined =
    item.variants?.[weight]?.[style]?.[subset]?.url;
  return urls?.woff2 ?? urls?.woff ?? urls?.ttf ?? '';
}

export class BaseFontsourceAsset extends BaseAsset {
  protected item: FontsourceListItem & { variants?: any };
  protected apiBaseUrl: string;

  constructor(item: FontsourceListItem & { variants?: any }, apiBaseUrl: string) {
    super();
    this.item = item;
    this.apiBaseUrl = apiBaseUrl;
  }

  getId(): string {
    return this.item.id;
  }

  getName(): string {
    return this.item.family;
  }

  setName(_name: string): void {
    console.warn('Fontsource assets cannot be renamed');
  }

  getMediaType(): MediaType {
    return MediaType.Font;
  }

  getImageUrlForCard(): string {
    return '';
  }

  getPreviewUrl(): string {
    return resolvePreviewUrl(this.item);
  }

  private async getAssetDataById(id: string): Promise<FontsourceDetailItem> {
    const url = `${this.apiBaseUrl}/${encodeURIComponent(id)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch font details: ${response.statusText}`);
    return response.json();
  }

  private getFontDownloadOption(fontData: FontsourceDetailItem): { subset: string; weight: number; style: string } {
    const defaultOption = {
      subset: fontData.defSubset ?? 'latin',
      weight: fontData.weights.includes(400) ? 400 : fontData.weights[0], // Pick the first available weight and style (or prefer 400/normal if present)
      style: fontData.styles.includes('normal') ? 'normal' : fontData.styles[0],
    }
    return defaultOption;
  }

  async getDownloadUrl(): Promise<string> {
    const fontData = await this.getAssetDataById(this.getId())

    const { subset, weight, style } = this.getFontDownloadOption(fontData);

    const urls = fontData.variants?.[weight]?.[style]?.[subset]?.url;

    return urls?.woff2 ?? urls?.woff ?? urls?.ttf ?? '';
  }

  async downloadFile(): Promise<{ blob: Blob; filename: string }> {
    const url = await this.getDownloadUrl();
    if (!url) return Promise.reject(new Error('No download URL available for this font'));
    return fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to download font: ${res.statusText}`);
        return res.blob();
      })
      .then(blob => ({ blob, filename: url }));
  }

  downloadFileVersion(_versionId: string): Promise<{ blob: Blob; filename: string }> {
    return Promise.reject(new Error('Versioning not supported'));
  }

  getMetadata(): IAssetMetadata {
    return {
      status: 'ready',
      date: this.item.lastModified ?? '',
      size: 0,
      id: this.item.id,
      licence: this.item.license,
      licenceUrl: 'https://github.com/fontsource/font-files/blob/main/LICENSE',
      categories: [this.item.category],
      type: this.item.type,
      fontFamily: this.item.family,
      defSubset: this.item.defSubset,
      styles: this.item.styles,
      subsets: this.item.subsets,
      weights: this.item.weights,
      variable: this.item.variable,
      variants: this.item.variants
    };
  }

  getSource(): string {
    return ExternalAssetSource.Fontsource;
  }

  getWorkspaceId(): string | undefined {
    return undefined;
  }
}
